/**
 * Create Invoice From Pricebook Modal
 *
 * Creates a Stripe invoice from a pricebook for a client or a firm (account).
 * When used for a firm without a linked Stripe customer, creates one and links it to the account.
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePricebooks } from '../../services/queries/pricebookQueries';
import { useCreateStripeInvoiceFromPricebook } from '../../services/queries/invoiceQueries';
import { finalizeStripeInvoice } from '../../services/stripeInvoiceService';
import { useStripeCustomerForClientOrAccount, useCreateOrphanStripeCustomer } from '../../services/queries/stripeCustomerQueries';
import { useToast } from '../../contexts/ToastContext';
import { shouldUseGobodo } from '../../utils/gobodoUtils';

interface CreateInvoiceFromPricebookModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, invoice is for this client. Omit when accountId is set (firm mode). */
  clientId?: string | number;
  clientName?: string;
  /** When set, invoice is for this firm (account). Stripe customer is looked up or created for the account. */
  accountId?: string | number;
  accountName?: string;
  /** Preselect this pricebook (e.g. when opened from pricebook detail). */
  preselectedPricebookId?: string;
  onSuccess?: (invoiceId?: string, paymentLink?: string) => void;
}

export const CreateInvoiceFromPricebookModal: React.FC<CreateInvoiceFromPricebookModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName,
  accountId,
  accountName,
  preselectedPricebookId,
  onSuccess,
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const entityId = accountId ?? clientId;
  const [pricebookIdOrKey, setPricebookIdOrKey] = useState('');
  const [sendNow, setSendNow] = useState(false);

  const { data: stripeCustomer } = useStripeCustomerForClientOrAccount(entityId);
  const { data: pricebooks = [] } = usePricebooks();
  const createFromPricebook = useCreateStripeInvoiceFromPricebook();
  const createOrphanCustomer = useCreateOrphanStripeCustomer();

  const customerId = stripeCustomer?.id;
  const stripeCustomerId = stripeCustomer?.accountID;
  const hasCustomer = customerId != null || (typeof stripeCustomerId === 'string' && stripeCustomerId.length > 0);

  useEffect(() => {
    if (isOpen && preselectedPricebookId) {
      setPricebookIdOrKey(preselectedPricebookId);
    }
  }, [isOpen, preselectedPricebookId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pricebookIdOrKey) {
      showError('Error', 'Please select a pricebook');
      return;
    }

    let effectiveCustomerId: number | undefined = customerId != null ? Number(customerId) : undefined;
    let effectiveStripeCustomerId: string | undefined = typeof stripeCustomerId === 'string' ? stripeCustomerId : undefined;

    if (!hasCustomer) {
      if (accountId != null) {
        const numericAccountId = Number(accountId);
        if (isNaN(numericAccountId)) {
          showError('Error', 'Invalid account id.');
          return;
        }
        try {
          const createResult = await createOrphanCustomer.mutateAsync({
            name: accountName ?? 'Firm',
            clientIdOrAccountId: numericAccountId,
          });
          const account = createResult.account;
          if (createResult.failureException || !account) {
            showError('Error', createResult.failureException ?? 'Failed to create Stripe customer for firm.');
            return;
          }
          effectiveCustomerId = account.id != null ? Number(account.id) : undefined;
          effectiveStripeCustomerId = typeof account.accountID === 'string' ? account.accountID : undefined;
          queryClient.invalidateQueries({ queryKey: ['stripeCustomerForClientOrAccount', accountId] });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          showError('Error', msg);
          return;
        }
      } else {
        showError('Error', 'This client has no linked Stripe customer. Create or link a Stripe customer first.');
        return;
      }
    }

    try {
      const result = await createFromPricebook.mutateAsync({
        pricebookIdOrKey,
        customerId: effectiveCustomerId,
        stripeCustomerId: effectiveStripeCustomerId,
        lineItems: [],
        sendAfterFinalize: sendNow,
      });

      let invoiceId = result.invoiceId;
      let paymentLink = result.hostInvoiceUrl;

      if (sendNow && invoiceId && typeof invoiceId === 'string' && invoiceId.startsWith('in_')) {
        try {
          const finalizeResult = await finalizeStripeInvoice(invoiceId);
          if (finalizeResult.failureException) {
            showError('Invoice created but finalize failed', finalizeResult.failureException);
          } else {
            const inv = finalizeResult.invoice && typeof finalizeResult.invoice === 'object' ? finalizeResult.invoice : (finalizeResult.invoice as any)?.invoice;
            const hostUrl = inv?.host_invoice_url ?? inv?.hostInvoiceUrl ?? inv?.invoice_url ?? inv?.invoiceUrl;
            if (typeof hostUrl === 'string' && hostUrl) paymentLink = hostUrl;
            success('Invoice created and finalized', 'Payment link is available. Share it with the customer.');
          }
        } catch (finalizeErr: unknown) {
          const msg = finalizeErr instanceof Error ? finalizeErr.message : String(finalizeErr);
          showError('Invoice created but finalize failed', msg);
        }
      } else {
        success('Invoice created', invoiceId ? `Invoice ${invoiceId} created successfully` : 'Invoice created');
      }

      onSuccess?.(invoiceId, paymentLink);
      onClose();
      setPricebookIdOrKey(preselectedPricebookId ?? '');
      setSendNow(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showError('Error', msg);
    }
  };

  if (!isOpen) return null;

  const isFirmMode = accountId != null;
  const canSubmit = hasCustomer || (isFirmMode && entityId != null);
  const isPending = createFromPricebook.isPending || createOrphanCustomer.isPending;

  if (!shouldUseGobodo()) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white dark:bg-[#1e293b] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Create Invoice from Pricebook
            </h2>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Create invoice from pricebook requires the backend (Gobodo) to be enabled.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#1e293b] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Create Invoice from Pricebook
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isFirmMode ? (
          accountName != null && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Firm: <span className="font-medium text-gray-900 dark:text-white">{accountName}</span>
            </p>
          )
        ) : (
          clientName != null && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Client: <span className="font-medium text-gray-900 dark:text-white">{clientName}</span>
            </p>
          )
        )}

        {!canSubmit && !isFirmMode ? (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 text-amber-800 dark:text-amber-200 text-sm">
            This client has no linked Stripe customer. Use &quot;Create Stripe customer&quot; or &quot;Link existing Stripe customer&quot; first, then create an invoice from a pricebook.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Pricebook
              </label>
              <select
                value={pricebookIdOrKey}
                onChange={(e) => setPricebookIdOrKey(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
                required
              >
                <option value="">Select a pricebook</option>
                {pricebooks.map((pb: { id?: string | number; name?: string; key?: string }) => (
                  <option key={String(pb.id ?? pb.key)} value={String(pb.id ?? pb.key ?? '')}>
                    {pb.name ?? pb.key ?? String(pb.id)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendNow"
                checked={sendNow}
                onChange={(e) => setSendNow(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="sendNow" className="text-sm text-gray-700 dark:text-gray-300">
                Finalize after create (payment link will be available)
              </label>
            </div>

            {isFirmMode && !hasCustomer && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This firm has no Stripe customer yet. One will be created and linked when you create the invoice.
              </p>
            )}
            {sendNow && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                The invoice will be finalized after creation so you can share the payment link with the customer.
              </p>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending || !pricebookIdOrKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? 'Creating…' : 'Create Invoice'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
